import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2, ArrowLeft, Users, BookOpen, Monitor, Clock,
  CheckCircle2, Circle, PlayCircle, ChevronRight, Zap, Eye, ChevronDown,
  ChevronLeft, Maximize, X as XIcon, LayoutGrid, Table2,
} from "lucide-react";
import { sanitizeHtml } from "@/utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatTime(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function StatusCell({ status }) {
  if (status === "complete") return <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />;
  if (status === "in_progress") return <PlayCircle className="h-4 w-4 text-amber-400 mx-auto" />;
  return <Circle className="h-4 w-4 text-[#3A3F4B] mx-auto" />;
}

function StatusDot({ status }) {
  if (status === "complete") return <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 flex-shrink-0" />;
  if (status === "in_progress") return <span className="inline-block h-2 w-2 rounded-full bg-amber-400 flex-shrink-0 animate-pulse" />;
  return <span className="inline-block h-2 w-2 rounded-full bg-[#2A2F3A] flex-shrink-0" />;
}

function Initials({ name, active }) {
  const letters = name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  return (
    <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[9px] font-bold font-body flex-shrink-0
      ${active ? "bg-amber-400/20 text-amber-400 ring-1 ring-amber-400/40" : "bg-white/5 text-[#6E7787]"}`}>
      {letters}
    </span>
  );
}

function ProgressBar({ done, inProg, total }) {
  if (!total) return null;
  const dPct = (done / total) * 100;
  const iPct = (inProg / total) * 100;
  return (
    <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-[#1A1F2A]">
      {dPct > 0 && <div className="bg-emerald-400 transition-all duration-500" style={{ width: `${dPct}%` }} />}
      {iPct > 0 && <div className="bg-amber-400 transition-all duration-500" style={{ width: `${iPct}%` }} />}
    </div>
  );
}

export default function ClassroomDashboard() {
  const [step, setStep] = useState("cohort");
  const [cohorts, setCohorts] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/cohorts?status=active`, { withCredentials: true })
      .then(({ data }) => setCohorts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selectCohort = async (cohort) => {
    setSelectedCohort(cohort);
    setStep("track");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/classroom/${cohort.id}/tracks`, { withCredentials: true });
      setTracks(data);
    } catch { setTracks([]); }
    finally { setLoading(false); }
  };

  const selectTrack = async (track) => {
    setSelectedTrack(track);
    setStep("view");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/classroom/${selectedCohort.id}/${track.id}/progress`, { withCredentials: true });
      setProgress(data);
    } catch { setProgress(null); }
    finally { setLoading(false); }
  };

  const applyEvent = useCallback((ev) => {
    setProgress((prev) => {
      if (!prev) return prev;
      const next = { ...prev, grid: { ...prev.grid }, time_on_task: { ...prev.time_on_task }, averages: { ...prev.averages } };
      const cid = ev.learner_id;
      const lid = ev.lexp_id;
      if (!next.grid[cid]) return next;
      if (ev.event_type === "standalone_lexp_viewed" || ev.event_type === "lesson_viewed") {
        if (next.grid[cid][lid] === "not_started") {
          next.grid[cid] = { ...next.grid[cid], [lid]: "in_progress" };
        }
      } else if (ev.event_type === "lexp_completed" || ev.event_type === "lesson_completed") {
        next.grid[cid] = { ...next.grid[cid], [lid]: "complete" };
      }
      return next;
    });
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (step !== "view" || !selectedCohort || !selectedTrack) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = new URL(process.env.REACT_APP_BACKEND_URL).host;
    // /api prefix routes through K8s ingress to the FastAPI backend pod.
    // Auth is via the httpOnly access_token cookie sent automatically on
    // same-origin WS handshake. Backend tenant-scopes the connection.
    const wsUrl = `${proto}//${host}/api/ws/classroom/${selectedCohort.id}/${selectedTrack.id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        if (ev.type === "event") applyEvent(ev);
      } catch { /* ignore */ }
    };
    ws.onerror = () => {};
    ws.onclose = () => {};
    return () => { ws.close(); wsRef.current = null; };
  }, [step, selectedCohort, selectedTrack, applyEvent]);

  const [mobileView, setMobileView] = useState("learner"); // "learner" | "lexp"
  const [selectedLearnerId, setSelectedLearnerId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [expandedLearners, setExpandedLearners] = useState({});
  const [cohortView, setCohortView] = useState(() => localStorage.getItem("classroom_view") || "card");
  const updateCohortView = (v) => { setCohortView(v); localStorage.setItem("classroom_view", v); };

  const toggleLearner = (id) => setExpandedLearners((prev) => ({ ...prev, [id]: !prev[id] }));

  const goBack = () => {
    if (step === "view") { setStep("track"); setProgress(null); setSelectedTrack(null); }
    else if (step === "track") { setStep("cohort"); setTracks([]); setSelectedCohort(null); }
  };

  return (
    <div className="space-y-6" data-testid="classroom-dashboard">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm font-body">
        <button onClick={() => { setStep("cohort"); setSelectedCohort(null); setSelectedTrack(null); setProgress(null); }}
          className={`${step === "cohort" ? "text-white font-medium" : "text-[#6E7787] hover:text-white cursor-pointer"}`}>
          Classroom
        </button>
        {selectedCohort && (
          <>
            <ChevronRight className="h-3.5 w-3.5 text-[#3A3F4B]" />
            <button onClick={() => { setStep("track"); setSelectedTrack(null); setProgress(null); }}
              className={`${step === "track" ? "text-white font-medium" : "text-[#6E7787] hover:text-white cursor-pointer"}`}>
              {selectedCohort.name}
            </button>
          </>
        )}
        {selectedTrack && (
          <>
            <ChevronRight className="h-3.5 w-3.5 text-[#3A3F4B]" />
            <span className="text-white font-medium">{selectedTrack.title}</span>
          </>
        )}
      </div>

      {step !== "cohort" && (
        <Button variant="ghost" size="sm" onClick={goBack} className="text-[#6E7787] hover:text-white -mt-2" data-testid="classroom-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
      )}

      {/* Step 1: Cohort Selection */}
      {step === "cohort" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-heading font-bold text-white mb-1">Classroom Dashboard</h1>
              <p className="text-sm text-[#6E7787] font-body">Select a cohort to monitor live progress.</p>
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" /></div>
          ) : cohorts.length === 0 ? (
            <p className="text-sm text-[#6E7787] font-body text-center py-12">No cohorts available.</p>
          ) : (
            <div className="rounded-xl overflow-hidden bg-[#11141A] border border-white/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Cohort</th>
                    <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Type</th>
                    <th className="text-right px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Learners</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((c) => (
                    <tr key={c.id} onClick={() => selectCohort(c)}
                      className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                      data-testid={`cohort-card-${c.id}`}>
                      <td className="px-4 py-3 text-sm font-body font-medium text-white">{c.name}</td>
                      <td className="px-4 py-3 text-xs font-body text-[#6E7787] capitalize">{(c.cohort_type || "standard").replace("_", " ")}</td>
                      <td className="px-4 py-3 text-right text-sm font-body text-[#A1A9B8]">{c.learner_count ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Track Selection */}
      {step === "track" && (
        <div>
          <h1 className="text-xl font-heading font-bold text-white mb-1">Select a Track</h1>
          <p className="text-sm text-[#6E7787] font-body mb-6">Choose a track to view learner progress.</p>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" /></div>
          ) : tracks.length === 0 ? (
            <p className="text-sm text-[#6E7787] font-body text-center py-12">No tracks assigned to this cohort.</p>
          ) : (
            <div className="rounded-xl overflow-hidden bg-[#11141A] border border-white/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Track</th>
                    <th className="text-right px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Items</th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((t) => (
                    <tr key={t.id} onClick={() => selectTrack(t)}
                      className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                      data-testid={`track-card-${t.id}`}>
                      <td className="px-4 py-3 text-sm font-body font-medium text-white">{t.title}</td>
                      <td className="px-4 py-3 text-right text-sm font-body text-[#A1A9B8]">{t.item_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Mode View */}
      {step === "view" && (
        <Tabs defaultValue="progress">
          <TabsList className="bg-[#0A0C10] border border-white/10 mb-6">
            <TabsTrigger value="progress" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-[#6E7787] font-body text-sm" data-testid="tab-progress">
              Progress
            </TabsTrigger>
            <TabsTrigger value="presenter" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-[#6E7787] font-body text-sm" data-testid="tab-presenter">
              Presenter
            </TabsTrigger>
          </TabsList>

          <TabsContent value="progress">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" /></div>
            ) : !progress ? (
              <p className="text-sm text-[#6E7787] font-body text-center py-12">Failed to load progress data.</p>
            ) : (
              <div className="space-y-8">
                {/* Live Progress Grid */}
                <div>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Monitor className="h-4 w-4" style={{ color: "var(--primary-color, #4A90D9)" }} />
                    <h2 className="font-body text-sm font-bold text-white">Live Progress Grid</h2>
                    <div className="flex items-center gap-3 ml-auto text-[10px] font-body text-[#6E7787]">
                      <span className="flex items-center gap-1"><Circle className="h-3 w-3 text-[#3A3F4B]" /> Not Started</span>
                      <span className="flex items-center gap-1"><PlayCircle className="h-3 w-3 text-amber-400" /> In Progress</span>
                      <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /> Complete</span>
                    </div>
                  </div>
                  {progress.learners.length === 0 ? (
                    <p className="text-sm text-[#6E7787] font-body text-center py-8">No learners enrolled.</p>
                  ) : (
                    <>
                      {/* Desktop: summary + expandable learner rows */}
                      <div className="hidden md:block space-y-2" data-testid="progress-grid">
                        {/* Overall cohort progress bar */}
                        {(() => {
                          const totalCells = progress.learners.length * progress.items.length;
                          const doneCount = progress.learners.reduce((acc, l) => acc + progress.items.filter((it) => (progress.grid[l.id] || {})[it.ref_id] === "complete").length, 0);
                          const inProgCount = progress.learners.reduce((acc, l) => acc + progress.items.filter((it) => (progress.grid[l.id] || {})[it.ref_id] === "in_progress").length, 0);
                          const pctDone = totalCells ? Math.round((doneCount / totalCells) * 100) : 0;
                          const pctInProg = totalCells ? Math.round((inProgCount / totalCells) * 100) : 0;
                          const activeLearners = progress.learners.filter((l) => progress.items.some((it) => (progress.grid[l.id] || {})[it.ref_id] === "in_progress")).length;
                          return (
                            <div className="bg-[#0D1017] border border-white/10 rounded-xl p-4 mb-2" data-testid="desktop-cohort-summary">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-4 text-xs font-body">
                                  <span className="text-[#A1A9B8]">{progress.learners.length} learners</span>
                                  <span className="text-[#A1A9B8]">{progress.items.length} items</span>
                                  <span className="flex items-center gap-1 text-amber-400"><Zap className="h-3 w-3" />{activeLearners} active now</span>
                                </div>
                                <span className="text-xs font-body text-emerald-400 font-semibold">{pctDone}% complete</span>
                              </div>
                              <div className="flex h-2 w-full rounded-full overflow-hidden bg-[#1A1F2A]">
                                {pctDone > 0 && <div className="bg-emerald-400 transition-all duration-500" style={{ width: `${pctDone}%` }} />}
                                {pctInProg > 0 && <div className="bg-amber-400 transition-all duration-500" style={{ width: `${pctInProg}%` }} />}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Expandable learner rows */}
                        {progress.learners.map((l) => {
                          const grid = progress.grid[l.id] || {};
                          const statuses = progress.items.map((it) => grid[it.ref_id] || "not_started");
                          const done = statuses.filter((s) => s === "complete").length;
                          const inProg = statuses.filter((s) => s === "in_progress").length;
                          const total = progress.items.length;
                          const currentItem = progress.items.find((it) => grid[it.ref_id] === "in_progress");
                          const isExpanded = expandedLearners[l.id];
                          return (
                            <div key={l.id} className="border border-white/10 rounded-xl overflow-hidden" data-testid={`grid-row-${l.id}`}>
                              <button onClick={() => toggleLearner(l.id)}
                                className="w-full flex items-center gap-4 px-4 py-3 bg-[#11141A] hover:bg-white/[0.02] transition-colors text-left"
                                data-testid={`expand-learner-${l.id}`}>
                                <Initials name={`${l.first_name} ${l.last_name}`} active={inProg > 0} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3 mb-1">
                                    <span className="text-sm font-body font-medium text-white">{l.first_name} {l.last_name}</span>
                                    {currentItem && (
                                      <span className="text-[11px] font-body text-amber-400 truncate">Working on: {currentItem.label}</span>
                                    )}
                                    {!currentItem && done === total && total > 0 && (
                                      <span className="text-[11px] font-body text-emerald-400">All complete</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="flex-1 max-w-xs"><ProgressBar done={done} inProg={inProg} total={total} /></div>
                                    <span className="text-[10px] font-body text-[#6E7787]">{done}/{total} done</span>
                                    <div className="flex items-center gap-1">
                                      {progress.items.map((it, idx) => (
                                        <StatusDot key={it.ref_id} status={grid[it.ref_id] || "not_started"} />
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-[#6E7787] transition-transform flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`} />
                              </button>
                              {isExpanded && (
                                <div className="bg-[#0A0C10] border-t border-white/10 px-4 py-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={`learner-detail-${l.id}`}>
                                  {progress.items.map((item) => {
                                    const status = grid[item.ref_id] || "not_started";
                                    return (
                                      <div key={item.ref_id} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border transition-colors
                                        ${status === "in_progress" ? "bg-amber-400/5 border-amber-400/20" : status === "complete" ? "bg-emerald-400/5 border-emerald-400/20" : "bg-[#11141A] border-white/5"}`}>
                                        <StatusCell status={status} />
                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm font-body text-white block truncate">{item.label}</span>
                                          <span className="text-[10px] font-body text-[#6E7787]">{item.type}</span>
                                        </div>
                                        <span className={`text-[10px] font-body font-semibold flex-shrink-0 ${status === "complete" ? "text-emerald-400" : status === "in_progress" ? "text-amber-400" : "text-[#3A3F4B]"}`}>
                                          {status === "complete" ? "Done" : status === "in_progress" ? "Active" : "—"}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Mobile: improved views */}
                      <div className="md:hidden space-y-3" data-testid="mobile-progress">
                        {/* Summary stats strip */}
                        {(() => {
                          const allStatuses = progress.learners.map((l) => {
                            const s = progress.items.map((it) => (progress.grid[l.id] || {})[it.ref_id] || "not_started");
                            const done = s.filter((x) => x === "complete").length;
                            const inP = s.filter((x) => x === "in_progress").length;
                            if (done === progress.items.length && progress.items.length > 0) return "all_done";
                            if (inP > 0 || done > 0) return "active";
                            return "idle";
                          });
                          const activeCt = allStatuses.filter((s) => s === "active").length;
                          const doneCt = allStatuses.filter((s) => s === "all_done").length;
                          const idleCt = allStatuses.filter((s) => s === "idle").length;
                          return (
                            <div className="flex items-center gap-2 bg-[#0D1017] border border-white/10 rounded-lg px-3 py-2.5" data-testid="mobile-stats-strip">
                              <div className="flex items-center gap-1.5 flex-1">
                                <Zap className="h-3.5 w-3.5 text-amber-400" />
                                <span className="text-xs font-body text-amber-400 font-semibold">{activeCt}</span>
                                <span className="text-[10px] font-body text-amber-300/80">active</span>
                              </div>
                              <div className="w-px h-4 bg-white/10" />
                              <div className="flex items-center gap-1.5 flex-1">
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                                <span className="text-xs font-body text-emerald-400 font-semibold">{doneCt}</span>
                                <span className="text-[10px] font-body text-emerald-300/80">finished</span>
                              </div>
                              <div className="w-px h-4 bg-white/10" />
                              <div className="flex items-center gap-1.5 flex-1">
                                <Eye className="h-3.5 w-3.5 text-[#8891A0]" />
                                <span className="text-xs font-body text-[#A1A9B8] font-semibold">{idleCt}</span>
                                <span className="text-[10px] font-body text-[#A1A9B8]">not started</span>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Toggle */}
                        <div className="flex bg-[#0A0C10] border border-white/10 rounded-lg p-0.5">
                          <button onClick={() => { setMobileView("learner"); setSelectedItemId(null); }}
                            className={`flex-1 text-xs font-body py-2 rounded-md transition-colors ${mobileView === "learner" ? "bg-white/10 text-white font-medium" : "text-[#6E7787]"}`}
                            data-testid="mobile-toggle-learner">
                            By Learner
                          </button>
                          <button onClick={() => { setMobileView("lexp"); setSelectedLearnerId(null); }}
                            className={`flex-1 text-xs font-body py-2 rounded-md transition-colors ${mobileView === "lexp" ? "bg-white/10 text-white font-medium" : "text-[#6E7787]"}`}
                            data-testid="mobile-toggle-lexp">
                            By LEXP
                          </button>
                        </div>

                        {/* By Learner — list */}
                        {mobileView === "learner" && !selectedLearnerId && (
                          <div className="space-y-2">
                            {progress.learners.map((l) => {
                              const grid = progress.grid[l.id] || {};
                              const statuses = progress.items.map((it) => grid[it.ref_id] || "not_started");
                              const done = statuses.filter((s) => s === "complete").length;
                              const inProg = statuses.filter((s) => s === "in_progress").length;
                              const total = progress.items.length;
                              const currentItem = progress.items.find((it) => grid[it.ref_id] === "in_progress");
                              return (
                                <button key={l.id} onClick={() => setSelectedLearnerId(l.id)}
                                  className="w-full bg-[#11141A] border border-white/10 rounded-xl p-3.5 text-left hover:border-white/20 transition-colors"
                                  data-testid={`mobile-learner-${l.id}`}>
                                  <div className="flex items-start gap-3">
                                    <Initials name={`${l.first_name} ${l.last_name}`} active={inProg > 0} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm font-body font-medium text-white truncate">{l.first_name} {l.last_name}</span>
                                        <ChevronRight className="h-3.5 w-3.5 text-[#3A3F4B] flex-shrink-0 ml-2" />
                                      </div>
                                      {/* Current activity line */}
                                      {currentItem ? (
                                        <p className="text-[11px] font-body text-amber-400 truncate mb-1.5">
                                          Working on: {currentItem.label}
                                        </p>
                                      ) : done === total && total > 0 ? (
                                        <p className="text-[11px] font-body text-emerald-400 mb-1.5">All items complete</p>
                                      ) : (
                                        <p className="text-[11px] font-body text-[#3A3F4B] mb-1.5">Not started</p>
                                      )}
                                      {/* Mini progress dots */}
                                      <div className="flex items-center gap-1 mb-1.5">
                                        {progress.items.map((it, idx) => (
                                          <StatusDot key={it.ref_id} status={grid[it.ref_id] || "not_started"} />
                                        ))}
                                      </div>
                                      {/* Progress bar with counts */}
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1"><ProgressBar done={done} inProg={inProg} total={total} /></div>
                                        <span className="text-[10px] font-body text-[#6E7787] flex-shrink-0">{done}/{total}</span>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* By Learner — drill-down */}
                        {mobileView === "learner" && selectedLearnerId && (() => {
                          const l = progress.learners.find((x) => x.id === selectedLearnerId);
                          if (!l) return null;
                          const grid = progress.grid[l.id] || {};
                          const done = progress.items.filter((it) => grid[it.ref_id] === "complete").length;
                          const total = progress.items.length;
                          return (
                            <div className="space-y-2">
                              <button onClick={() => setSelectedLearnerId(null)} className="text-xs font-body text-[#6E7787] hover:text-white flex items-center gap-1 mb-1" data-testid="mobile-back-learners">
                                <ArrowLeft className="h-3 w-3" /> All learners
                              </button>
                              <div className="flex items-center gap-3 mb-2">
                                <Initials name={`${l.first_name} ${l.last_name}`} active={false} />
                                <div className="flex-1">
                                  <p className="text-sm font-body font-bold text-white">{l.first_name} {l.last_name}</p>
                                  <p className="text-[10px] font-body text-[#6E7787]">{done}/{total} complete</p>
                                </div>
                              </div>
                              {progress.items.map((item) => {
                                const status = grid[item.ref_id] || "not_started";
                                return (
                                  <div key={item.ref_id} className={`flex items-center gap-3 rounded-lg px-4 py-3 border transition-colors
                                    ${status === "in_progress" ? "bg-amber-400/5 border-amber-400/20" : status === "complete" ? "bg-emerald-400/5 border-emerald-400/20" : "bg-[#11141A] border-white/10"}`}>
                                    <StatusCell status={status} />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-sm font-body text-white block truncate">{item.label}</span>
                                      <span className="text-[10px] font-body text-[#6E7787]">{item.type}</span>
                                    </div>
                                    <span className={`text-[10px] font-body font-semibold flex-shrink-0 ${status === "complete" ? "text-emerald-400" : status === "in_progress" ? "text-amber-400" : "text-[#3A3F4B]"}`}>
                                      {status === "complete" ? "Done" : status === "in_progress" ? "Active" : "—"}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}

                        {/* By LEXP — list */}
                        {mobileView === "lexp" && !selectedItemId && (
                          <div className="space-y-2">
                            {progress.items.map((item) => {
                              const learnerStatuses = progress.learners.map((l) => ({
                                ...l,
                                status: (progress.grid[l.id] || {})[item.ref_id] || "not_started",
                              }));
                              const done = learnerStatuses.filter((x) => x.status === "complete");
                              const active = learnerStatuses.filter((x) => x.status === "in_progress");
                              const idle = learnerStatuses.filter((x) => x.status === "not_started");
                              const total = progress.learners.length;
                              return (
                                <button key={item.ref_id} onClick={() => setSelectedItemId(item.ref_id)}
                                  className="w-full bg-[#11141A] border border-white/10 rounded-xl p-3.5 text-left hover:border-white/20 transition-colors"
                                  data-testid={`mobile-lexp-${item.ref_id}`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="min-w-0 flex-1">
                                      <span className="text-sm font-body font-medium text-white block truncate">{item.label}</span>
                                      <span className="text-[10px] font-body text-[#6E7787]">{item.type}</span>
                                    </div>
                                    <ChevronRight className="h-3.5 w-3.5 text-[#3A3F4B] flex-shrink-0 mt-0.5" />
                                  </div>
                                  {/* Progress bar */}
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className="flex-1"><ProgressBar done={done.length} inProg={active.length} total={total} /></div>
                                    <span className="text-[10px] font-body text-[#6E7787] flex-shrink-0">{done.length}/{total}</span>
                                  </div>
                                  {/* Active learners inline */}
                                  {active.length > 0 && (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <Zap className="h-3 w-3 text-amber-400 flex-shrink-0" />
                                      {active.slice(0, 3).map((a) => (
                                        <span key={a.id} className="text-[10px] font-body text-amber-400">{a.first_name}</span>
                                      ))}
                                      {active.length > 3 && <span className="text-[10px] font-body text-amber-400/60">+{active.length - 3}</span>}
                                    </div>
                                  )}
                                  {active.length === 0 && done.length > 0 && idle.length === 0 && (
                                    <p className="text-[10px] font-body text-emerald-400">All learners complete</p>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* By LEXP — drill-down */}
                        {mobileView === "lexp" && selectedItemId && (() => {
                          const item = progress.items.find((x) => x.ref_id === selectedItemId);
                          if (!item) return null;
                          const learnerStatuses = progress.learners.map((l) => ({
                            ...l,
                            status: (progress.grid[l.id] || {})[item.ref_id] || "not_started",
                          }));
                          const active = learnerStatuses.filter((x) => x.status === "in_progress");
                          const done = learnerStatuses.filter((x) => x.status === "complete");
                          const idle = learnerStatuses.filter((x) => x.status === "not_started");
                          const groups = [
                            { label: "Active Now", items: active, color: "amber" },
                            { label: "Complete", items: done, color: "emerald" },
                            { label: "Not Started", items: idle, color: "gray" },
                          ].filter((g) => g.items.length > 0);
                          return (
                            <div className="space-y-3">
                              <button onClick={() => setSelectedItemId(null)} className="text-xs font-body text-[#6E7787] hover:text-white flex items-center gap-1" data-testid="mobile-back-lexps">
                                <ArrowLeft className="h-3 w-3" /> All items
                              </button>
                              <div className="mb-1">
                                <p className="text-sm font-body font-bold text-white">{item.label}</p>
                                <p className="text-[10px] font-body text-[#6E7787]">{item.type}</p>
                              </div>
                              {groups.map((group) => (
                                <div key={group.label}>
                                  <p className={`text-[10px] font-body font-semibold uppercase tracking-wider mb-1.5 ${
                                    group.color === "amber" ? "text-amber-400" : group.color === "emerald" ? "text-emerald-400" : "text-[#3A3F4B]"
                                  }`}>
                                    {group.label} ({group.items.length})
                                  </p>
                                  <div className="space-y-1">
                                    {group.items.map((person) => (
                                      <div key={person.id} className={`flex items-center gap-3 rounded-lg px-3.5 py-2.5 border ${
                                        group.color === "amber" ? "bg-amber-400/5 border-amber-400/20" : group.color === "emerald" ? "bg-emerald-400/5 border-emerald-400/20" : "bg-[#11141A] border-white/10"
                                      }`}>
                                        <Initials name={`${person.first_name} ${person.last_name}`} active={group.color === "amber"} />
                                        <span className="text-sm font-body text-white">{person.first_name} {person.last_name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </>
                  )}
                </div>

                {/* Time on Task */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4" style={{ color: "var(--primary-color, #4A90D9)" }} />
                    <h2 className="font-body text-sm font-bold text-white">Time on Task</h2>
                  </div>
                  {progress.items.filter((i) => i.type === "lexp").length === 0 ? (
                    <p className="text-sm text-[#6E7787] font-body text-center py-8">No LEXPs in this track.</p>
                  ) : (
                    <>
                      {/* Desktop: table */}
                      <div className="hidden md:block overflow-x-auto border border-white/10 rounded-xl">
                        <table className="w-full" data-testid="time-on-task">
                          <thead>
                            <tr className="border-b border-white/10 bg-[#0A0C10]">
                              <th className="text-left px-3 py-2.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider sticky left-0 bg-[#0A0C10] z-10 min-w-[160px]">Learner</th>
                              {progress.items.filter((i) => i.type === "lexp").map((item) => (
                                <th key={item.ref_id} className="text-center px-3 py-2.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider min-w-[100px]">
                                  <span className="truncate block max-w-[100px]" title={item.label}>{item.label}</span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-white/10 bg-[#0A0C10]/50">
                              <td className="px-3 py-2.5 text-xs font-body font-bold text-[#A1A9B8] uppercase tracking-wider sticky left-0 bg-[#0A0C10]/50 z-10">
                                Cohort Average
                              </td>
                              {progress.items.filter((i) => i.type === "lexp").map((item) => (
                                <td key={item.ref_id} className="px-3 py-2.5 text-center">
                                  <span className="text-xs font-body font-bold" style={{ color: "var(--primary-color, #4A90D9)" }}>
                                    {formatTime(progress.averages[item.ref_id])}
                                  </span>
                                </td>
                              ))}
                            </tr>
                            {progress.learners.map((l) => (
                              <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                                <td className="px-3 py-2.5 text-sm text-white font-body font-medium sticky left-0 bg-[#11141A] z-10">
                                  {l.first_name} {l.last_name}
                                </td>
                                {progress.items.filter((i) => i.type === "lexp").map((item) => (
                                  <td key={item.ref_id} className="px-3 py-2.5 text-center">
                                    <span className="text-xs font-body text-[#A1A9B8]">
                                      {formatTime((progress.time_on_task[l.id] || {})[item.ref_id])}
                                    </span>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Mobile: card-based time on task */}
                      <div className="md:hidden space-y-2" data-testid="mobile-time-on-task">
                        {/* Cohort average card */}
                        <div className="bg-[#0D1017] border border-white/10 rounded-xl p-3.5">
                          <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-[#6E7787] mb-2">Cohort Average</p>
                          <div className="space-y-1.5">
                            {progress.items.filter((i) => i.type === "lexp").map((item) => (
                              <div key={item.ref_id} className="flex items-center justify-between">
                                <span className="text-xs font-body text-[#A1A9B8] truncate flex-1 mr-2">{item.label}</span>
                                <span className="text-xs font-body font-bold flex-shrink-0" style={{ color: "var(--primary-color, #4A90D9)" }}>
                                  {formatTime(progress.averages[item.ref_id])}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Per-learner cards */}
                        {progress.learners.map((l) => (
                          <div key={l.id} className="bg-[#11141A] border border-white/10 rounded-xl p-3.5">
                            <div className="flex items-center gap-2 mb-2">
                              <Initials name={`${l.first_name} ${l.last_name}`} active={false} />
                              <span className="text-sm font-body font-medium text-white">{l.first_name} {l.last_name}</span>
                            </div>
                            <div className="space-y-1.5">
                              {progress.items.filter((i) => i.type === "lexp").map((item) => (
                                <div key={item.ref_id} className="flex items-center justify-between">
                                  <span className="text-xs font-body text-[#A1A9B8] truncate flex-1 mr-2">{item.label}</span>
                                  <span className="text-xs font-body text-[#A1A9B8] flex-shrink-0">
                                    {formatTime((progress.time_on_task[l.id] || {})[item.ref_id])}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="presenter">
            <PresenterMode cohortId={selectedCohort?.id} trackId={selectedTrack?.id} onClose={() => {}} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}


function PresenterMode({ cohortId, trackId }) {
  const [slides, setSlides] = useState([]);
  const [trackTitle, setTrackTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!cohortId || !trackId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/classroom/${cohortId}/${trackId}/presenter`, { withCredentials: true });
        setSlides(data.slides || []);
        setTrackTitle(data.track_title || "");
      } catch (err) { console.error("Presenter load error:", err); }
      finally { setLoading(false); }
    })();
  }, [cohortId, trackId]);

  // Navigation: currentSlide = LEXP index, currentQ = question index within that LEXP
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentQ, setCurrentQ] = useState(0);

  const slide = slides[currentSlide] || null;
  const questions = slide?.questions || [];
  const qCount = questions.length;
  const currentQuestion = questions[currentQ] || null;

  const prevQ = () => { if (currentQ > 0) setCurrentQ(currentQ - 1); };
  const nextQ = () => { if (currentQ < qCount - 1) setCurrentQ(currentQ + 1); };
  const prevSlide = () => { if (currentSlide > 0) { setCurrentSlide(currentSlide - 1); setCurrentQ(0); } };
  const nextSlide = () => { if (currentSlide < slides.length - 1) { setCurrentSlide(currentSlide + 1); setCurrentQ(0); } };

  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      if (e.key === "ArrowLeft") prevQ();
      else if (e.key === "ArrowRight") nextQ();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, currentQ, qCount]);

  const enterFullscreen = () => {
    setActive(true);
    try { document.documentElement.requestFullscreen(); } catch {}
  };

  const exitPresenter = () => {
    setActive(false);
    setCurrentSlide(0);
    setCurrentQ(0);
    try { if (document.fullscreenElement) document.exitFullscreen(); } catch {}
  };

  useEffect(() => {
    const handler = () => { if (!document.fullscreenElement && active) setActive(false); };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, [active]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" /></div>;
  if (slides.length === 0) return <p className="text-sm text-[#6E7787] font-body text-center py-12">No items in this track.</p>;

  // Launch card
  if (!active) {
    const totalQ = slides.reduce((a, s) => a + (s.questions || []).length, 0);
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-[#11141A] border border-white/10 rounded-xl" data-testid="presenter-launch">
        <Monitor className="h-10 w-10 text-[#6E7787] mb-4" />
        <h3 className="font-body text-base font-bold text-white mb-1">Presenter Mode</h3>
        <p className="text-sm text-[#6E7787] font-body mb-6 text-center max-w-sm">{trackTitle} — {slides.length} activit{slides.length === 1 ? "y" : "ies"}, {totalQ} question{totalQ !== 1 ? "s" : ""}</p>
        <Button onClick={enterFullscreen} className="gap-2 font-body" style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "#fff" }} data-testid="presenter-enter-btn">
          <Maximize className="h-4 w-4" /> Enter Full Screen
        </Button>
      </div>
    );
  }

  const isTask = currentQuestion?.item_type === "task";

  return (
    <div ref={containerRef} className="fixed inset-0 z-[9999] bg-[#F8F9FC] flex flex-col select-none" style={{ fontSize: "22px" }} data-testid="presenter-overlay">
      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-2 bg-white border-b border-gray-200 flex-shrink-0">
        <span style={{ fontSize: "14px" }} className="font-heading font-bold tracking-wide text-gray-400">DiNA<span className="text-gray-900">XLP</span></span>
        <div className="flex-1 text-center">
          <h1 style={{ fontSize: "16px" }} className="font-heading font-bold text-gray-900">{trackTitle}</h1>
        </div>
        <button onClick={exitPresenter} className="text-gray-400 hover:text-gray-700 transition-colors p-2" data-testid="presenter-close-btn">
          <XIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Main container — two cards side by side */}
      <div className="flex-1 flex gap-5 p-5 min-h-0 overflow-hidden">
        {/* Left card — 60% — Learning content */}
        <div className="w-[60%] bg-white border border-gray-200 rounded-2xl flex flex-col overflow-hidden" data-testid="presenter-left-panel">
          <div className="px-6 pt-5 pb-3 flex-shrink-0 border-b border-gray-100">
            <p style={{ fontSize: "11px" }} className="font-body uppercase tracking-widest text-gray-400 mb-1.5">
              Activity {slide.index + 1} of {slides.length} — {slide.type === "lexp" ? "Learning Experience" : "Assessment"}
            </p>
            <h2 style={{ fontSize: "20px" }} className="font-heading font-bold text-gray-900">{slide.label}</h2>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {slide.description && (
              <div style={{ fontSize: "18px", lineHeight: "1.7" }} className="text-gray-600 font-body ql-rendered mb-4" dangerouslySetInnerHTML={{ __html: sanitizeHtml(slide.description) }} />
            )}
            {slide.patient_scenario && (
              <div style={{ fontSize: "18px", lineHeight: "1.7" }} className="text-gray-800 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(slide.patient_scenario) }} />
            )}
            {slide.tasks && (
              <div className="mt-4">
                <h3 style={{ fontSize: "11px" }} className="font-body font-semibold text-gray-400 uppercase tracking-widest mb-2">Tasks</h3>
                <div style={{ fontSize: "18px", lineHeight: "1.7" }} className="text-gray-800 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(slide.tasks) }} />
              </div>
            )}
          </div>
        </div>

        {/* Right card — 40% — Questions */}
        <div className="w-[40%] bg-white border border-gray-200 rounded-2xl flex flex-col overflow-hidden" data-testid="presenter-right-panel">
          <div className="px-6 pt-5 pb-3 flex-shrink-0 border-b border-gray-100">
            <p style={{ fontSize: "11px" }} className="font-body uppercase tracking-widest text-gray-400 mb-1.5">
              {slide.label}
            </p>
            <h2 style={{ fontSize: "20px" }} className="font-heading font-bold text-gray-900">
              {qCount > 0 ? `${isTask ? "Task" : "Question"} ${currentQ + 1} of ${qCount}` : "No Questions"}
            </h2>
          </div>

          {/* Question body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {qCount === 0 ? (
              <p style={{ fontSize: "18px" }} className="text-gray-400 font-body italic pt-8 text-center">No questions for this activity.</p>
            ) : isTask ? (
              <div style={{ fontSize: "18px", lineHeight: "1.7" }} className="text-gray-800 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentQuestion.stem) }} />
            ) : (
              <>
                <div style={{ fontSize: "18px", lineHeight: "1.7" }} className="text-gray-900 font-body font-medium ql-rendered mb-5" dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentQuestion.stem) }} />
                {currentQuestion.answer_options && currentQuestion.answer_options.length > 0 && (
                  <div className="space-y-2.5">
                    {currentQuestion.answer_options.map((opt, oi) => (
                      <div key={opt.option_id || oi} className="flex items-start gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50" style={{ fontSize: "18px" }}>
                        <span className="h-7 w-7 rounded-full border-2 border-gray-300 flex-shrink-0 mt-0.5 flex items-center justify-center font-bold text-gray-400" style={{ fontSize: "13px" }}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        <span className="flex-1 font-body text-gray-700">{opt.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Question nav buttons */}
          {qCount > 1 && (
            <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
              <button onClick={prevQ} disabled={currentQ === 0}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-body text-white transition-all ${currentQ === 0 ? "opacity-30 cursor-default" : "hover:opacity-90 cursor-pointer"}`}
                style={{ fontSize: "13px", backgroundColor: "var(--primary-color, #4A90D9)" }}>
                <ChevronLeft className="h-4 w-4" /> Previous
              </button>
              <span style={{ fontSize: "12px" }} className="font-body text-gray-400 tabular-nums">{currentQ + 1} / {qCount}</span>
              <button onClick={nextQ} disabled={currentQ === qCount - 1}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-body text-white transition-all ${currentQ === qCount - 1 ? "opacity-30 cursor-default" : "hover:opacity-90 cursor-pointer"}`}
                style={{ fontSize: "13px", backgroundColor: "var(--primary-color, #4A90D9)" }}>
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom bar — LEXP-level navigation */}
      <div className="flex items-center justify-between px-8 py-2 bg-white border-t border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          {slides.map((s, i) => (
            <button key={s.ref_id} onClick={() => { setCurrentSlide(i); setCurrentQ(0); }}
              className={`h-1.5 rounded-full transition-all ${i === currentSlide ? "w-5 bg-gray-900" : "w-1.5 bg-gray-300 hover:bg-gray-400"}`} />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={prevSlide} disabled={currentSlide === 0}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-body text-white transition-all ${currentSlide === 0 ? "opacity-30 cursor-default" : "hover:opacity-90 cursor-pointer"}`}
            style={{ fontSize: "13px", backgroundColor: "var(--primary-color, #4A90D9)" }}
            data-testid="presenter-prev">
            <ChevronLeft className="h-4 w-4" /> Prev Activity
          </button>
          <span style={{ fontSize: "13px" }} className="font-body text-gray-500 tabular-nums">Activity {currentSlide + 1} of {slides.length}</span>
          <button onClick={nextSlide} disabled={currentSlide === slides.length - 1}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-body text-white transition-all ${currentSlide === slides.length - 1 ? "opacity-30 cursor-default" : "hover:opacity-90 cursor-pointer"}`}
            style={{ fontSize: "13px", backgroundColor: "var(--primary-color, #4A90D9)" }}
            data-testid="presenter-next">
            Next Activity <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
